import {
    BarDataReceivedHandler,
    ExternalMarketDataStrategy,
    RefDataReceivedHandler,
    RefDataReceivedHandlerOptions,
    TickDataReceivedHandler,
} from 'marketData/ExternalMarketDataStrategy';
import {
    BloombergMarketDataRequest,
    isBloombergTickRequest,
    requestUid,
} from 'marketData/MarketDataRequest';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';
import { SidecarApi } from '@instinet-bcng/sidecar-client';
import {
    ActionsIntervalDispatcher,
    ActionsIntervalDispatcherFactory,
} from 'store/ActionsDispatcher';
import { EmbeddedApiService } from 'embedded/api/EmbeddedApiService';
import { SidecarTickRequestManager } from './requests/SidecarTickRequestManager';
import { SidecarBarRequestManager } from './requests/SidecarBarRequestManager';
import { SidecarRequestManager } from './requests/SidecarRequestManager';
import { showInfoAsync, showWarningAsync } from 'notification/store/thunks';
import { AnyAction, AnyThunkAction } from 'store/store';
import {
    addMessage,
    BarData,
    clearMessages,
    setExternalMarketDataSource,
    TickData,
} from 'marketData/store/marketDataSlice';

interface Options {
    actionsIntervalDispatcherFactory: ActionsIntervalDispatcherFactory;
    embeddedApiService: EmbeddedApiService;
    loggingService: LoggingService;
}

/*
 * If we are running in OpenFin, and the mktDataSidecar query parameter is set,
 * we use this strategy for external market data,
 * which uses the Sidecar c# app as a bridge to Bloomberg
 */
export class SidecarMarketDataStrategy implements ExternalMarketDataStrategy {
    public readonly actionsIntervalDispatcherFactory: ActionsIntervalDispatcherFactory;
    public readonly actionsIntervalDispatcher: ActionsIntervalDispatcher;
    private readonly loggingService: LoggingService;
    private readonly api: SidecarApi;
    private logger: Logger;

    private requestManagers: SidecarRequestManager<BloombergMarketDataRequest>[] =
        [];

    private tickDataReceivedHandler: undefined | TickDataReceivedHandler =
        undefined;
    private barDataReceivedHandler: undefined | BarDataReceivedHandler =
        undefined;
    private refDataReceivedHandler: undefined | RefDataReceivedHandler =
        undefined;

    public constructor(options: Options) {
        const logger = buildLogger(
            'SidecarMarketDataStrategy',
            options.loggingService
        );
        this.logger = logger;
        this.loggingService = options.loggingService;
        this.actionsIntervalDispatcherFactory =
            options.actionsIntervalDispatcherFactory;
        this.actionsIntervalDispatcher =
            options.actionsIntervalDispatcherFactory.new();

        const api = new SidecarApi();
        this.api = api;
        api.onConnectionStatusChanged(
            this.onConnectionStatusChanged.bind(this)
        );
        (async () => {
            try {
                logger.debug('Connecting to sidecar...');
                await api.connect({ retryOnConnectionError: true, wait: true });
                logger.debug('Connected');
            } catch (err) {
                logger.critical('Error connecting to SideCar', err);
            }
        })();
    }

    public subscribe(request: BloombergMarketDataRequest): void {
        const options = {
            api: this.api,
            loggingService: this.loggingService,
            dispatcher: this.actionsIntervalDispatcher,
        };

        let manager = this.findRequestManager(request);

        if (!manager) {
            manager = isBloombergTickRequest(request)
                ? new SidecarTickRequestManager({
                      ...options,
                      request,
                      onData: this.onTickData.bind(this),
                  })
                : new SidecarBarRequestManager({
                      ...options,
                      request,
                      onBarData: this.onBarData.bind(this),
                      onRefData: this.onRefData.bind(this),
                  });

            this.requestManagers.push(manager);
        }

        this.logger.debug('Subscribing', {
            request,
            managers: this.requestManagers.map((r) => r.uid),
        });

        if (this.api.isConnected) {
            manager.subscribe();
        } else {
            this.logger.debug(
                'Will subscribe for market data once Bloomberg connection established',
                request
            );
        }
    }

    public unsubscribe(request: BloombergMarketDataRequest): void {
        const existing = this.findRequestManager(request);
        if (existing) {
            (async () => existing.unsubscribe())();
        }
    }
    public onMarketBarDataReceived(handler: BarDataReceivedHandler): void {
        this.barDataReceivedHandler = handler;
    }
    public onMarketTickDataReceived(handler: TickDataReceivedHandler): void {
        this.tickDataReceivedHandler = handler;
    }
    public onRefDataReceived(handler: RefDataReceivedHandler): void {
        this.refDataReceivedHandler = handler;
    }

    private onConnectionStatusChanged() {
        const isConnected = this.api.isConnected;

        this.logger.info('Connection status changed to ' + isConnected);

        if (!isConnected) {
            // Lost connection
            this.dispatchImmediate(
                showWarningAsync('Sidecar Connection Lost') as AnyThunkAction
            );
            this.dispatchImmediate(addMessage('Sidecar Connection Lost'));
        }

        if (isConnected) {
            this.dispatchImmediate(
                showInfoAsync(
                    'Bloomberg Connection Established'
                ) as AnyThunkAction
            );

            this.onConnected();
        }
    }

    private findRequestManager(
        request: BloombergMarketDataRequest | undefined
    ) {
        if (!request) {
            return undefined;
        }
        return this.requestManagers.find((r) => r.uid === requestUid(request));
    }

    private dispatchImmediate(
        action: { payload: unknown; type: string } | AnyThunkAction
    ) {
        const dispatcher = this.actionsIntervalDispatcherFactory.new();
        dispatcher.dispatch(action as unknown as AnyAction);
        dispatcher.dispose();
    }

    private onConnected() {
        this.dispatchImmediate(setExternalMarketDataSource('Sidecar'));
        this.dispatchImmediate(clearMessages());

        for (const managers of this.requestManagers) {
            if (managers.status !== 'Running') {
                managers.subscribe();
            }
        }
    }

    private onTickData(data: TickData[]) {
        if (!this.tickDataReceivedHandler) {
            this.logger.critical('No tick data handler received');
            return;
        }
        this.tickDataReceivedHandler(data);
    }
    private onBarData(data: BarData[]) {
        if (!this.barDataReceivedHandler) {
            this.logger.critical('No bar data handler received');
            return;
        }
        this.barDataReceivedHandler(data);
    }
    private onRefData(options: RefDataReceivedHandlerOptions) {
        if (!this.refDataReceivedHandler) {
            this.logger.critical('No ref data handler received');
            return;
        }
        this.refDataReceivedHandler(options);
    }
}
